import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface TopMarqueeContextData {
  isTopMarqueeVisible: boolean
  isMenuOpen: boolean
  isClosedByUser: boolean
  showAppBanner: boolean
  hideTopMarquee: () => void
  showTopMarquee: () => void
  setMenuOpen: (isOpen: boolean) => void
  closeTopMarqueeByUser: () => void
  closeAppBanner: () => void
}

const TopMarqueeContext = createContext<TopMarqueeContextData>({} as TopMarqueeContextData)

interface TopMarqueeProviderProps {
  children: ReactNode
}

export const TopMarqueeProvider: React.FC<TopMarqueeProviderProps> = ({ children }) => {
  // Estado do banner do app (mobile)
  const [showAppBanner, setShowAppBanner] = useState<boolean>(() => {
    try {
      return !window.localStorage.getItem('appBannerDismissed')
    } catch (e) {
      return true
    }
  })

  // Estado do menu principal
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  
  // Estado de fechado pelo usuário (persiste durante a sessão)
  const [isClosedByUser, setIsClosedByUser] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem('topMarqueeDismissed') === '1'
    } catch (e) {
      return false
    }
  })

  // Visibilidade do TopMarquee (considerando menu e fechamento pelo usuário)
  const [isTopMarqueeVisible, setIsTopMarqueeVisible] = useState<boolean>(() => {
    try {
      const dismissedByUser = window.sessionStorage.getItem('topMarqueeDismissed') === '1'
      return !dismissedByUser
    } catch (e) {
      return true
    }
  })

  // Atualiza a visibilidade baseado no estado do menu e se foi fechado pelo usuário
  useEffect(() => {
    if (isClosedByUser) {
      setIsTopMarqueeVisible(false)
    } else {
      // Se não foi fechado pelo usuário, mostra/esconde baseado no menu
      setIsTopMarqueeVisible(!isMenuOpen)
    }
  }, [isMenuOpen, isClosedByUser])

  // Gerencia as classes CSS no document.documentElement
  useEffect(() => {
    const root = document.documentElement
    
    if (isTopMarqueeVisible) {
      root.classList.add('has-top-marquee')
    } else {
      root.classList.remove('has-top-marquee')
    }
    
    if (showAppBanner) {
      root.classList.add('has-app-banner')
    } else {
      root.classList.remove('has-app-banner')
    }

    return () => {
      root.classList.remove('has-top-marquee')
      root.classList.remove('has-app-banner')
    }
  }, [isTopMarqueeVisible, showAppBanner])

  const hideTopMarquee = () => {
    setIsTopMarqueeVisible(false)
  }

  const showTopMarquee = () => {
    if (!isClosedByUser) {
      setIsTopMarqueeVisible(true)
    }
  }

  const setMenuOpen = (isOpen: boolean) => {
    setIsMenuOpen(isOpen)
  }

  const closeTopMarqueeByUser = () => {
    setIsClosedByUser(true)
    setIsTopMarqueeVisible(false)
    try {
      window.sessionStorage.setItem('topMarqueeDismissed', '1')
    } catch (e) {
      // noop
    }
  }

  const closeAppBanner = () => {
    setShowAppBanner(false)
    try {
      window.localStorage.setItem('appBannerDismissed', '1')
    } catch (e) {
      // noop
    }
  }

  return (
    <TopMarqueeContext.Provider
      value={{
        isTopMarqueeVisible,
        isMenuOpen,
        isClosedByUser,
        showAppBanner,
        hideTopMarquee,
        showTopMarquee,
        setMenuOpen,
        closeTopMarqueeByUser,
        closeAppBanner
      }}>
      {children}
    </TopMarqueeContext.Provider>
  )
}

export const useTopMarquee = (): TopMarqueeContextData => {
  const context = useContext(TopMarqueeContext)
  if (!context) {
    throw new Error('useTopMarquee deve ser usado dentro de um TopMarqueeProvider')
  }
  return context
}
